/**
 * Utils TUI App
 * Professional TUI interface with left sidebar menu
 */
import React, { useState } from 'react';
import { Box, Text, useInput, useApp } from 'ink';
import { PasswordView } from './views/PasswordView.js';
import { UuidView } from './views/UuidView.js';
import { HashView } from './views/HashView.js';
import { Base64View } from './views/Base64View.js';
import { UrlView } from './views/UrlView.js';
import { UnixTimeView } from './views/UnixTimeView.js';
import { JwtView } from './views/JwtView.js';
import { CronView } from './views/CronView.js';
import { TimezoneView } from './views/TimezoneView.js';
import { HttpView } from './views/HttpView.js';
import { MarkdownView } from './views/MarkdownView.js';

export type UtilityType =
    | 'password'
    | 'uuid'
    | 'hash'
    | 'base64-encode'
    | 'base64-decode'
    | 'http'
    | 'jwt'
    | 'unix-time'
    | 'timezone'
    | 'url-encode'
    | 'url-decode'
    | 'cron'
    | 'markdown';

interface MenuItem {
    id: UtilityType;
    icon: string;
    label: string;
    description: string;
}

const MENU_ITEMS: MenuItem[] = [
    { id: 'password', icon: '🔐', label: 'Password', description: 'Generate secure passwords' },
    { id: 'uuid', icon: '🆔', label: 'UUID', description: 'Generate UUID v4' },
    { id: 'hash', icon: '🔒', label: 'Hash', description: 'Hash with MD5/SHA/bcrypt' },
    { id: 'base64-encode', icon: '📝', label: 'Base64 Encode', description: 'Encode to Base64' },
    { id: 'base64-decode', icon: '📝', label: 'Base64 Decode', description: 'Decode Base64' },
    { id: 'http', icon: '🌐', label: 'HTTP Request', description: 'Make HTTP requests' },
    { id: 'jwt', icon: '🔑', label: 'JWT', description: 'Decode/encode JWT' },
    { id: 'unix-time', icon: '🕒', label: 'Unix Time', description: 'Convert timestamps' },
    { id: 'timezone', icon: '🌍', label: 'Timezone', description: 'Convert timezones' },
    { id: 'url-encode', icon: '🔗', label: 'URL Encode', description: 'Encode URLs' },
    { id: 'url-decode', icon: '🔗', label: 'URL Decode', description: 'Decode URLs' },
    { id: 'cron', icon: '⏰', label: 'Cron Parser', description: 'Parse cron expressions' },
    { id: 'markdown', icon: '📄', label: 'Markdown', description: 'Preview markdown' },
];

interface UtilsAppProps {
    onExit: () => void;
}

export const UtilsApp: React.FC<UtilsAppProps> = ({ onExit }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeView, setActiveView] = useState<UtilityType | null>(null);
    const { exit } = useApp();

    useInput((input, key) => {
        if (activeView) {
            // If in a view, Escape goes back to menu
            if (key.escape) {
                setActiveView(null);
            }
            return;
        }

        // Main menu navigation
        if (key.upArrow || input === 'k') {
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : MENU_ITEMS.length - 1));
        } else if (key.downArrow || input === 'j') {
            setSelectedIndex((prev) => (prev < MENU_ITEMS.length - 1 ? prev + 1 : 0));
        } else if (key.return) {
            setActiveView(MENU_ITEMS[selectedIndex].id);
        } else if ((input === 'q' && key.ctrl) || key.escape) {
            onExit();
            exit();
        }
    });

    return (
        <Box flexDirection="column" width="100%" height="100%">
            {/* Header */}
            <Box
                borderStyle="single"
                borderColor="cyan"
                paddingX={2}
                paddingY={0}
                marginBottom={1}
            >
                <Text bold color="cyan">
                    🛠️  Developer Utilities - Interactive Mode
                </Text>
                <Box marginLeft="auto">
                    <Text dimColor>Press Ctrl+Q to quit</Text>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Box flexGrow={1}>
                {/* Left Sidebar - Menu */}
                <Box
                    flexDirection="column"
                    width={30}
                    borderStyle="single"
                    borderColor="gray"
                    paddingX={1}
                    paddingY={1}
                    marginRight={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="yellow">
                            Select Utility:
                        </Text>
                    </Box>

                    {MENU_ITEMS.map((item, index) => (
                        <Box key={item.id} marginBottom={0}>
                            <Text
                                color={selectedIndex === index && !activeView ? 'green' : undefined}
                                bold={selectedIndex === index && !activeView}
                                dimColor={activeView !== null && activeView !== item.id}
                            >
                                {selectedIndex === index && !activeView ? '▶ ' : '  '}
                                {item.icon} {item.label}
                            </Text>
                        </Box>
                    ))}

                    <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={1}>
                        <Text dimColor>
                            ↑/↓: Navigate{'\n'}
                            Enter: Select{'\n'}
                            Esc: Back/Quit
                        </Text>
                    </Box>
                </Box>

                {/* Right Content Area */}
                <Box
                    flexDirection="column"
                    flexGrow={1}
                    borderStyle="single"
                    borderColor="gray"
                    paddingX={2}
                    paddingY={1}
                >
                    {activeView ? (
                        <ActiveUtilityView utilityType={activeView} />
                    ) : (
                        <WelcomeView selectedItem={MENU_ITEMS[selectedIndex]} />
                    )}
                </Box>
            </Box>

            {/* Footer */}
            <Box
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                marginTop={1}
            >
                <Text dimColor>
                    jai1-cli | Use arrow keys or j/k to navigate
                </Text>
            </Box>
        </Box>
    );
};

// Welcome view shown when no utility is selected
const WelcomeView: React.FC<{ selectedItem: MenuItem }> = ({ selectedItem }) => {
    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    Welcome to Developer Utilities
                </Text>
            </Box>

            <Box marginBottom={2}>
                <Text>
                    Select a utility from the left menu to get started.
                </Text>
            </Box>

            <Box
                borderStyle="single"
                borderColor="yellow"
                paddingX={2}
                paddingY={1}
                marginBottom={2}
            >
                <Box flexDirection="column">
                    <Text bold color="yellow">
                        {selectedItem.icon} {selectedItem.label}
                    </Text>
                    <Text dimColor>{selectedItem.description}</Text>
                </Box>
            </Box>

            <Box marginBottom={1}>
                <Text bold>Quick Actions:</Text>
            </Box>

            <Box flexDirection="column" marginLeft={2}>
                <Text>• Press <Text color="green">Enter</Text> to open selected utility</Text>
                <Text>• Use <Text color="green">↑/↓</Text> or <Text color="green">j/k</Text> to navigate</Text>
                <Text>• Press <Text color="green">Esc</Text> to go back or quit</Text>
                <Text>• Press <Text color="green">Ctrl+Q</Text> to quit anytime</Text>
            </Box>

            <Box marginTop={2}>
                <Text dimColor>
                    💡 Tip: Each utility provides an interactive interface for easy usage.
                </Text>
            </Box>
        </Box>
    );
};

// Active utility view - renders the appropriate component
const ActiveUtilityView: React.FC<{ utilityType: UtilityType }> = ({ utilityType }) => {
    switch (utilityType) {
        case 'password':
            return <PasswordView />;
        case 'uuid':
            return <UuidView />;
        case 'hash':
            return <HashView />;
        case 'base64-encode':
        case 'base64-decode':
            return <Base64View />;
        case 'url-encode':
        case 'url-decode':
            return <UrlView />;
        case 'unix-time':
            return <UnixTimeView />;
        case 'jwt':
            return <JwtView />;
        case 'cron':
            return <CronView />;
        case 'timezone':
            return <TimezoneView />;
        case 'http':
            return <HttpView />;
        case 'markdown':
            return <MarkdownView />;
        default:
            return <PlaceholderView utilityType={utilityType} />;
    }
};

// Placeholder for utilities not yet implemented
const PlaceholderView: React.FC<{ utilityType: UtilityType }> = ({ utilityType }) => {
    const item = MENU_ITEMS.find((m) => m.id === utilityType);

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    {item?.icon} {item?.label}
                </Text>
            </Box>

            <Box borderStyle="single" borderColor="yellow" paddingX={2} paddingY={1}>
                <Text>
                    🚧 This utility view is under construction.{'\n'}
                    {'\n'}
                    For now, use the command-line version:{'\n'}
                    <Text color="green">$ jai1 utils {utilityType} --help</Text>
                    {'\n'}
                    {'\n'}
                    Press <Text color="yellow">Esc</Text> to return to the menu.
                </Text>
            </Box>
        </Box>
    );
};
